import React, { useState, useEffect, useMemo } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api, API_BASE_URL } from '../api';
import { useToast } from '../components/Toast';
import { getStoredUser, clearStoredUser } from '../utils/safeStorage';

const money = (n) => {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return '$0.00';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const initials = (name, email) => {
  if (name && name.trim()) {
    return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
  }
  return (email || 'U').charAt(0).toUpperCase();
};

const bookingStatusClass = (status) => {
  if (status === 'confirmed') return 'bg-cream text-primary';
  if (status === 'pending') return 'bg-sand text-ink';
  if (status === 'cancelled') return 'bg-red-100 text-red-800';
  if (status === 'completed') return 'bg-primary/10 text-primary';
  return 'bg-sand text-ink';
};

const orderStatusClass = (status) => {
  if (status === 'delivered') return 'bg-cream text-primary';
  if (status === 'shipped') return 'bg-primary/10 text-primary';
  if (status === 'processing') return 'bg-sand text-ink';
  if (status === 'cancelled') return 'bg-red-100 text-red-800';
  return 'bg-sand text-ink';
};

const toList = (data) => (Array.isArray(data) ? data : (data?.results || []));

const CustomerPortal = ({ navigate, user, onLogout }) => {
  const toast = useToast();
  const stored = getStoredUser();
  const [email, setEmail] = useState(stored?.email || '');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [itemOrders, setItemOrders] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const paymentMethod = 'paynow';
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [customerData, setCustomerData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState('bookings');
  const [documents, setDocuments] = useState([]);
  const [docUpload, setDocUpload] = useState({ document_type: 'passport', title: '', file: null });
  const [docUploading, setDocUploading] = useState(false);
  const [docFileKey, setDocFileKey] = useState(0);
  const [expandedBookings, setExpandedBookings] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  const [loginLoading, setLoginLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const loadPortalData = async (userEmail) => {
    const [bookingsData, customerDataRes, itemOrdersData, documentsData] = await Promise.all([
      api.getCustomerBookings(userEmail),
      api.getCustomer(userEmail),
      api.getCustomerItemOrders(userEmail).catch(() => []),
      api.getCustomerDocuments(userEmail).catch(() => []),
    ]);
    return {
      bookings: toList(bookingsData),
      customers: toList(customerDataRes),
      orders: toList(itemOrdersData),
      docs: toList(documentsData),
    };
  };

  const applyPortalData = (data) => {
    setBookings(data.bookings);
    setItemOrders(data.orders);
    setDocuments(data.docs);
    setCustomer(data.customers[0] || null);
    setCustomerData(data.customers[0] || {});
    setIsLoggedIn(true);
    setLoadError('');
  };

  useEffect(() => {
    const updated = localStorage.getItem('orders_updated');
    if (!updated || !isLoggedIn || !email) return;
    localStorage.removeItem('orders_updated');
    api.getCustomerItemOrders(email)
      .then((data) => {
        setItemOrders(toList(data));
        setActiveTab('shop-orders');
      })
      .catch(() => {});
  }, [isLoggedIn, email]);

  useEffect(() => {
    const boot = async () => {
      const savedUser = getStoredUser();
      if (!savedUser?.email) {
        setLoading(false);
        return;
      }
      setEmail(savedUser.email);
      try {
        const data = await loadPortalData(savedUser.email);
        applyPortalData(data);
        localStorage.removeItem('orders_updated');
      } catch (error) {
        console.error('Error loading user data:', error);
        setLoadError(error.message || 'Unable to load your dashboard. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, []);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoginLoading(true);
    setLoadError('');
    try {
      const data = await loadPortalData(email.trim());
      if (data.bookings.length || data.customers.length || data.orders.length || data.docs.length) {
        applyPortalData(data);
        toast.success('Welcome to your dashboard');
      } else {
        toast.error('No bookings or orders found for this email. Register or book a package first.');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Unable to load your information. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleAddPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    if (amount > parseFloat(selectedBooking.balance_amount)) {
      toast.error('Payment amount cannot exceed the remaining balance');
      return;
    }
    if (paymentMethod === 'paynow' && !paymentScreenshot) {
      toast.error('Please upload a payment screenshot for PayNow');
      return;
    }
    if (paymentScreenshot && paymentScreenshot.size > 5 * 1024 * 1024) {
      toast.error('Screenshot must be 5MB or smaller');
      return;
    }

    setPaymentLoading(true);
    try {
      const result = await api.addPayment(selectedBooking.id, {
        amount: paymentAmount,
        payment_method: paymentMethod,
        payment_screenshot: paymentScreenshot,
      }, email);

      if (result?.error) {
        toast.error(result.error);
        return;
      }

      toast.success('Payment submitted successfully');
      setPaymentAmount('');
      setPaymentScreenshot(null);
      setSelectedBooking(null);
      const bookingsData = await api.getCustomerBookings(email);
      setBookings(toList(bookingsData));
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Error processing payment. Please try again.');
      setPaymentScreenshot(null);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!customer?.id) {
      toast.error('Profile could not be saved. Please try again later.');
      return;
    }
    setProfileSaving(true);
    try {
      await api.updateCustomer(customer.id, customerData);
      toast.success('Profile updated successfully');
      setEditMode(false);
      setCustomer(customerData);
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.message || 'Error updating profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const retryLoad = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await loadPortalData(email);
      applyPortalData(data);
    } catch (error) {
      setLoadError(error.message || 'Unable to load your dashboard.');
    } finally {
      setLoading(false);
    }
  };

  const displayName = user?.name || customer?.contact_name || customerData?.email || email;
  const totalOutstanding = useMemo(
    () => bookings.reduce((sum, b) => sum + (parseFloat(b.balance_amount) || 0), 0),
    [bookings]
  );
  const upcoming = useMemo(
    () => bookings.find((b) => b.status === 'confirmed' || b.status === 'pending') || bookings[0],
    [bookings]
  );

  const logoutPortal = () => {
    setIsLoggedIn(false);
    setEmail('');
    setBookings([]);
    setItemOrders([]);
    setDocuments([]);
    setCustomer(null);
    if (typeof onLogout === 'function') onLogout();
    else {
      clearStoredUser();
      navigate('landing');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar navigate={navigate} />
        <div className="max-w-7xl mx-auto px-4 pt-28 pb-16">
          <div className="h-36 rounded-2xl bg-slate-200 animate-pulse mb-6" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-slate-200 animate-pulse" />)}
          </div>
          <div className="h-64 rounded-2xl bg-slate-200 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <Navbar navigate={navigate} />
        <div className="flex-1 flex items-center justify-center px-4 pt-28 pb-16">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-cream text-primary flex items-center justify-center text-2xl font-bold">
              {initials(user?.name, email || 'U')}
            </div>
            <h1 className="text-2xl font-bold text-center text-slate-900 mb-1">Customer Dashboard</h1>
            <p className="text-slate-500 text-center text-sm mb-6">
              Enter the email used for your booking to view trips, payments, and documents.
            </p>
            {loadError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{loadError}</div>
            )}
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink/80 mb-1.5">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>
              <button onClick={handleLogin} disabled={loginLoading}
                className="w-full gradient-gold hover:opacity-90 text-ink py-3 rounded-md font-semibold disabled:opacity-50">
                {loginLoading ? 'Opening dashboard…' : 'Access dashboard'}
              </button>
            </form>
            <p className="text-center text-sm text-slate-500 mt-5">
              New here?{' '}
              <button onClick={() => navigate('register')} className="text-primary font-semibold hover:underline">Create an account</button>
              {' · '}
              <button onClick={() => navigate('login')} className="text-primary font-semibold hover:underline">Login</button>
            </p>
          </div>
        </div>
        <Footer navigate={navigate} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Navbar navigate={navigate} />

      <div className="bg-gradient-to-br from-ink via-primary to-secondary text-white pt-28 pb-16 relative overflow-hidden">
        <div className="absolute -right-16 top-10 w-48 h-48 border border-accent/20 rotate-45" />
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row md:items-end justify-between gap-6 relative">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center text-2xl font-bold">
              {initials(displayName, email)}
            </div>
            <div>
              <p className="text-sand text-sm">Assalamu alaikum</p>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold break-words">{displayName}</h1>
              <p className="text-sand text-sm mt-1">{email}</p>
            </div>
          </div>
          <button
            onClick={logoutPortal}
            className="self-start md:self-auto bg-white/10 hover:bg-white/20 border border-white/20 px-5 py-2.5 rounded-lg font-medium text-sm">
            Sign out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-8 pb-16 flex-1 w-full">
        {loadError && (
          <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
            <span className="text-sm">{loadError}</span>
            <button onClick={retryLoad} className="font-semibold underline text-sm shrink-0">Retry</button>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Bookings', value: bookings.length, sub: 'Umrah & Hajj trips' },
            { label: 'Outstanding', value: money(totalOutstanding), sub: 'Remaining balance' },
            { label: 'Shop orders', value: itemOrders.length, sub: 'Travel essentials' },
            { label: 'Documents', value: documents.length, sub: 'Visa, tickets & more' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-5">
              <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-slate-400">{stat.label}</p>
              <p className="text-lg sm:text-2xl font-bold text-slate-900 mt-1 break-words">{stat.value}</p>
              <p className="text-xs text-slate-500 mt-1">{stat.sub}</p>
            </div>
          ))}
        </div>

        {upcoming && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Upcoming trip</p>
                <h2 className="text-xl font-bold text-slate-900">{upcoming.package_name || 'Your booking'}</h2>
                <p className="text-sm text-slate-500 mt-0.5">Booking #{upcoming.booking_number}</p>
              </div>
              <span className={`self-start px-3 py-1 rounded-full text-xs font-bold ${bookingStatusClass(upcoming.status)}`}>
                {(upcoming.status || '').toUpperCase()}
              </span>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Paid {money(upcoming.paid_amount)}</span>
                <span>Total {money(upcoming.total_amount)}</span>
              </div>
              <div className="h-2 bg-sand rounded-full overflow-hidden">
                <div
                  className="h-full bg-cream0 rounded-full"
                  style={{
                    width: `${Math.min(100, (parseFloat(upcoming.paid_amount) / (parseFloat(upcoming.total_amount) || 1)) * 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 lg:sticky lg:top-24">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-lg font-bold text-slate-900">Profile</h2>
                {!editMode && (
                  <button onClick={() => setEditMode(true)} className="text-primary text-sm font-semibold hover:underline">Edit</button>
                )}
              </div>

              {editMode ? (
                <div className="space-y-3">
                  {[
                    { key: 'email', label: 'Email', type: 'email' },
                    { key: 'phone', label: 'Phone', type: 'tel' },
                    { key: 'city', label: 'City', type: 'text' },
                    { key: 'country', label: 'Country', type: 'text' },
                    { key: 'unit_number', label: 'Unit number', type: 'text' },
                    { key: 'postal_code', label: 'Postal code', type: 'text' },
                  ].map((field) => (
                    <div key={field.key}>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{field.label}</label>
                      <input
                        type={field.type}
                        value={customerData[field.key] || ''}
                        onChange={(e) => setCustomerData({ ...customerData, [field.key]: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Address</label>
                    <textarea
                      value={customerData.address || ''}
                      onChange={(e) => setCustomerData({ ...customerData, address: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                      rows="2"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={handleUpdateCustomer} disabled={profileSaving}
                      className="flex-1 bg-primary text-white py-2 rounded-lg text-sm font-semibold hover:bg-secondary disabled:opacity-50">
                      {profileSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => { setEditMode(false); setCustomerData(customer || {}); }}
                      className="flex-1 border border-slate-300 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  {[
                    ['Email', customer?.email],
                    ['Phone', customer?.phone],
                    ['Address', customer?.address],
                    ['Unit', customer?.unit_number],
                    ['City', customer?.city],
                    ['Country', customer?.country],
                    ['Postal code', customer?.postal_code],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label}>
                      <p className="text-xs text-slate-400">{label}</p>
                      <p className="text-ink font-medium break-words">{value}</p>
                    </div>
                  ))}
                  {!customer && <p className="text-slate-400 text-sm">Profile details will appear after your first booking.</p>}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex gap-1 mb-6 bg-white rounded-xl p-1.5 shadow-sm border border-slate-100 overflow-x-auto touch-scroll">
              {[
                { id: 'bookings', label: `Bookings (${bookings.length})` },
                { id: 'shop-orders', label: `Shop orders (${itemOrders.length})` },
                { id: 'documents', label: `Documents (${documents.length})` },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-[120px] py-2.5 px-3 rounded-md text-sm font-semibold transition-all ${
                    activeTab === tab.id ? 'bg-ink text-accent shadow' : 'text-slate-600 hover:bg-cream'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'bookings' && (
              bookings.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
                  <p className="text-slate-500 mb-4">You have no bookings yet.</p>
                  <button onClick={() => navigate('category', { category: 'umrah-packages' })}
                    className="bg-primary hover:bg-secondary text-white px-6 py-2.5 rounded-lg font-semibold">
                    Browse packages
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => {
                    const isExpanded = expandedBookings[booking.id];
                    const paidPct = Math.min(100, (parseFloat(booking.paid_amount) / (parseFloat(booking.total_amount) || 1)) * 100);
                    return (
                      <div key={booking.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                        <button
                          className="w-full text-left p-4 sm:p-6 hover:bg-slate-50 transition-colors"
                          onClick={() => setExpandedBookings((p) => ({ ...p, [booking.id]: !p[booking.id] }))}
                        >
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-base sm:text-lg font-bold text-slate-900">{booking.package_name}</h3>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${bookingStatusClass(booking.status)}`}>
                                  {(booking.status || '').toUpperCase()}
                                </span>
                              </div>
                              <p className="text-slate-500 text-sm mt-1">#{booking.booking_number}</p>
                            </div>
                            <div className="text-left sm:text-right shrink-0">
                              <p className="text-lg sm:text-xl font-bold text-slate-900">{money(booking.total_amount)}</p>
                              <p className="text-xs text-primary mt-1">{isExpanded ? 'Hide details' : 'View details'}</p>
                            </div>
                          </div>
                          <div className="mt-4">
                            <div className="h-1.5 bg-sand rounded-full overflow-hidden">
                              <div className="h-full bg-cream0" style={{ width: `${paidPct}%` }} />
                            </div>
                            <div className="flex justify-between text-xs text-slate-500 mt-2">
                              <span>Paid {money(booking.paid_amount)}</span>
                              <span className={parseFloat(booking.balance_amount) > 0 ? 'text-red-600 font-semibold' : 'text-primary'}>
                                Balance {money(booking.balance_amount)}
                              </span>
                            </div>
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="px-4 sm:px-6 pb-6 border-t border-slate-100 pt-4">
                            {booking.rooms?.length > 0 && (
                              <div className="mb-4">
                                <h4 className="font-bold text-sm mb-2">Rooms & passengers</h4>
                                <div className="space-y-3">
                                  {booking.rooms.map((room) => (
                                    <div key={room.id} className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                                      <div className="flex justify-between mb-2">
                                        <div>
                                          <p className="font-semibold">Room {room.room_number}</p>
                                          <p className="text-xs text-slate-500">{(room.sharing_type || '').replace('_', ' ').toUpperCase()}</p>
                                        </div>
                                        <p className="font-bold">{money(room.subtotal)}</p>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                                        <div className="bg-white p-2 rounded-lg text-center"><p className="text-slate-500">Adults</p><p className="font-semibold">{room.num_adults}</p></div>
                                        <div className="bg-white p-2 rounded-lg text-center"><p className="text-slate-500">Children</p><p className="font-semibold">{room.num_children}</p></div>
                                        <div className="bg-white p-2 rounded-lg text-center"><p className="text-slate-500">Infants</p><p className="font-semibold">{room.num_infants}</p></div>
                                      </div>
                                      {room.passengers?.map((pax) => (
                                        <div key={pax.id} className="text-sm bg-white p-2 rounded-lg flex flex-col sm:flex-row sm:justify-between gap-1 mb-1">
                                          <span><span className="font-medium">{pax.full_name}</span> <span className="text-slate-400">({pax.passenger_type})</span></span>
                                          <span className="text-slate-500 break-all">{pax.passport_number}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <button
                              onClick={() => window.open(`${API_BASE_URL}/bookings/${booking.id}/invoice/?email=${encodeURIComponent(email)}`, '_blank')}
                              className="w-full bg-slate-800 hover:bg-slate-900 text-white py-2.5 rounded-lg font-semibold text-sm mb-3">
                              Download invoice (PDF)
                            </button>

                            {parseFloat(booking.balance_amount) > 0 && (
                              selectedBooking?.id === booking.id ? (
                                <div className="border border-sand bg-cream/50 rounded-xl p-4">
                                  <h4 className="font-bold mb-3">Make a payment</h4>
                                  <label className="block text-sm font-medium mb-1">Payment method</label>
                                  <div className="w-full border rounded-lg px-3 py-2 mb-3 bg-sand/40 font-medium">PayNow (Singapore)</div>

                                  {paymentMethod === 'paynow' && (
                                    <div className="mb-4 p-4 bg-white rounded-xl border border-purple-100 text-center">
                                      <p className="font-semibold text-purple-800 text-sm mb-3">Scan to pay with PayNow</p>
                                      <img src="/paynow-qr.jpeg" alt="PayNow QR Code" className="w-40 h-auto mx-auto" />
                                      <p className="text-sm font-semibold mt-3">UEN: 199402129H</p>
                                      <p className="text-xs text-slate-500">TM Fouzy Travel & Tours Pte Ltd</p>
                                    </div>
                                  )}

                                  <label className="block text-sm font-medium mb-1">Amount (max {money(booking.balance_amount)})</label>
                                  <input
                                    type="number" min="0" max={booking.balance_amount} step="0.01"
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    disabled={!!paymentScreenshot}
                                    className="w-full border rounded-lg px-3 py-2 mb-3 disabled:bg-sand"
                                    placeholder="0.00"
                                  />

                                  {paymentMethod === 'paynow' && (
                                    <div className="mb-3">
                                      <label className="block text-sm font-medium mb-1">Payment screenshot <span className="text-red-500">*</span></label>
                                      <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (file && file.size > 5 * 1024 * 1024) {
                                            toast.error('File must be 5MB or smaller');
                                            e.target.value = '';
                                            return;
                                          }
                                          setPaymentScreenshot(file || null);
                                        }}
                                        className="w-full border rounded-lg px-3 py-2 text-sm"
                                      />
                                      {paymentScreenshot && (
                                        <p className="text-xs text-primary mt-1">Selected: {paymentScreenshot.name}</p>
                                      )}
                                    </div>
                                  )}

                                  <div className="flex gap-2">
                                    <button onClick={handleAddPayment} disabled={paymentLoading}
                                      className="flex-1 bg-primary text-white py-2.5 rounded-lg font-semibold disabled:opacity-50">
                                      {paymentLoading ? 'Processing…' : 'Confirm payment'}
                                    </button>
                                    <button onClick={() => { setSelectedBooking(null); setPaymentAmount(''); setPaymentScreenshot(null); }}
                                      className="flex-1 border py-2.5 rounded-lg font-semibold hover:bg-white">
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button onClick={() => setSelectedBooking(booking)}
                                  className="w-full bg-primary text-white py-2.5 rounded-lg font-semibold hover:bg-secondary">
                                  Make payment
                                </button>
                              )
                            )}

                            {booking.payments?.length > 0 && (
                              <div className="border-t mt-4 pt-4">
                                <h4 className="font-bold text-sm mb-2">Payment history</h4>
                                <div className="space-y-2">
                                  {booking.payments.map((payment) => (
                                    <div key={payment.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm p-3 bg-slate-50 rounded-lg">
                                      <div>
                                        <p className="font-semibold">{payment.payment_number}</p>
                                        <p className="text-xs text-slate-500">
                                          {payment.created_at ? new Date(payment.created_at).toLocaleString() : ''} · {payment.payment_method_display}
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <p className="font-bold text-primary">{money(payment.amount)}</p>
                                        <button
                                          onClick={() => window.open(`${API_BASE_URL}/payments/${payment.id}/receipt/?email=${encodeURIComponent(email)}`, '_blank')}
                                          className="mt-1 text-xs bg-slate-800 text-white px-3 py-1 rounded">
                                          Receipt
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {activeTab === 'shop-orders' && (
              itemOrders.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
                  <p className="text-slate-500 mb-4">No shop orders yet.</p>
                  <button onClick={() => navigate('shop')} className="bg-primary hover:bg-secondary text-white px-6 py-2.5 rounded-lg font-semibold">
                    Browse shop
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {itemOrders.map((order) => {
                    const isExpanded = expandedOrders[order.id];
                    return (
                      <div key={order.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                        <button className="w-full text-left p-6 hover:bg-slate-50" onClick={() => setExpandedOrders((p) => ({ ...p, [order.id]: !p[order.id] }))}>
                          <div className="flex justify-between items-start gap-3">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-lg font-bold">Order #{order.order_number}</h3>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${orderStatusClass(order.status)}`}>
                                  {(order.status || '').toUpperCase()}
                                </span>
                              </div>
                              <p className="text-sm text-slate-500 mt-1">
                                {order.created_at ? new Date(order.created_at).toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                              </p>
                            </div>
                            <p className="text-xl font-bold text-primary">{money(order.total_amount)}</p>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-6 pb-6 border-t pt-4">
                            <div className="space-y-3">
                              {order.items?.map((item) => (
                                <div key={item.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                                  {item.item_image && <img src={item.item_image} alt={item.item_name} className="w-14 h-14 object-cover rounded-lg" />}
                                  <div className="flex-1">
                                    <p className="font-semibold">{item.item_name}</p>
                                    <p className="text-sm text-slate-500">Qty {item.quantity}</p>
                                  </div>
                                  <p className="font-bold">{money(item.subtotal)}</p>
                                </div>
                              ))}
                            </div>
                            {order.shipping_address && (
                              <div className="border-t mt-4 pt-4 text-sm text-slate-600">
                                <p className="font-semibold text-ink mb-1">Shipping</p>
                                <p>{order.shipping_address}</p>
                                {order.shipping_unit && <p>Unit: {order.shipping_unit}</p>}
                                {order.shipping_postal && <p>Postal: {order.shipping_postal}</p>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {activeTab === 'documents' && (
              <div className="space-y-4">
                <form
                  className="bg-white rounded-2xl border border-slate-100 p-5"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!docUpload.title || !docUpload.file) {
                      toast.error('Add a title and choose a file');
                      return;
                    }
                    try {
                      setDocUploading(true);
                      await api.uploadCustomerDocument({
                        email,
                        customer_id: customer?.id,
                        document_type: docUpload.document_type,
                        title: docUpload.title,
                        file: docUpload.file,
                      });
                      const docs = await api.getCustomerDocuments(email);
                      setDocuments(docs);
                      setDocUpload({ document_type: 'passport', title: '', file: null });
                      setDocFileKey((key) => key + 1);
                      toast.success('Document sent. The agency can now view it.');
                    } catch (error) {
                      toast.error(error.message || 'Upload failed');
                    } finally {
                      setDocUploading(false);
                    }
                  }}
                >
                  <h3 className="font-bold text-slate-900 mb-1">Upload a document</h3>
                  <p className="text-sm text-slate-500 mb-4">Passport, visa, or any file you want the agency to see.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select
                      value={docUpload.document_type}
                      onChange={(e) => setDocUpload({ ...docUpload, document_type: e.target.value })}
                      className="border rounded-lg px-3 py-2"
                    >
                      <option value="passport">Passport</option>
                      <option value="visa">Visa</option>
                      <option value="ticket">Flight ticket</option>
                      <option value="hotel">Hotel voucher</option>
                      <option value="insurance">Insurance</option>
                      <option value="vaccination">Vaccination</option>
                      <option value="other">Other</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Document title"
                      value={docUpload.title}
                      onChange={(e) => setDocUpload({ ...docUpload, title: e.target.value })}
                      className="border rounded-lg px-3 py-2"
                    />
                  </div>
                  <input
                    key={docFileKey}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,image/*"
                    className="mt-3 w-full text-sm"
                    onChange={(e) => setDocUpload({ ...docUpload, file: e.target.files?.[0] || null })}
                  />
                  <button
                    type="submit"
                    disabled={docUploading}
                    className="mt-4 bg-primary hover:bg-secondary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                  >
                    {docUploading ? 'Uploading...' : 'Send to agency'}
                  </button>
                </form>
              {documents.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
                  <p className="text-slate-500 mb-1">No documents yet</p>
                  <p className="text-sm text-slate-400">Files you upload, and documents from our team, will appear here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="bg-white rounded-xl border border-slate-100 p-4 flex justify-between items-center gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-slate-900 truncate">{doc.title}</h3>
                          {doc.is_important && <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full">IMPORTANT</span>}
                        </div>
                        <p className="text-sm text-slate-500">{doc.document_type_display}</p>
                        {doc.description && <p className="text-sm text-slate-400 mt-0.5">{doc.description}</p>}
                        <p className="text-xs text-slate-400 mt-1">
                          Uploaded {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''}
                          {doc.expiry_date && ` · Expires ${new Date(doc.expiry_date).toLocaleDateString()}`}
                        </p>
                      </div>
                      {doc.file_url && (
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                          className="shrink-0 bg-primary hover:bg-secondary text-white px-4 py-2 rounded-lg text-sm font-semibold">
                          Download
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
};

export default CustomerPortal;
